import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import ChatContainer from '../components/ChatContainer';
import ScheduleVisitModal from '../components/ScheduleVisitModal';
import api from '../services/api';
import useSessionTranscript from '../hooks/useSessionTranscript';
import './CompoundPage.css';

const EGP = (n) =>
  typeof n === 'number' && !Number.isNaN(n)
    ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M EGP`
    : null;

/** Map the IndexingTool /compounds/{id}/details payload to the fields this page renders. */
function mapDetails(d) {
  const units = Array.isArray(d.unit_types) ? d.unit_types : [];
  const roomValues = units.map((u) => u.no_of_rooms).filter((r) => r != null);
  const unitTypes = roomValues.length
    ? `${Math.min(...roomValues)}–${Math.max(...roomValues)} BR`
    : units.map((u) => u.unit_type).filter(Boolean).join(', ') || null;

  const areaMins = units.map((u) => u.area_min).filter((a) => a != null);
  const areaMaxs = units.map((u) => u.area_max).filter((a) => a != null);
  const areaRange = areaMins.length && areaMaxs.length
    ? `${Math.round(Math.min(...areaMins))}–${Math.round(Math.max(...areaMaxs))} m²`
    : null;

  const deliveryDates = units.flatMap((u) => u.delivery_dates || []).filter(Boolean);
  const delivery = deliveryDates.length ? [...new Set(deliveryDates)].sort()[0] : null;

  const priceFrom = EGP(d.price_range?.min);

  return {
    id: d.compound_id,
    project_name: d.project_name,
    location: d.location,
    images: d.images || [],
    price_from: priceFrom,
    unit_types: unitTypes,
    delivery,
    area_range: areaRange,
    payment_summary: d.payment_plans,
    facilities: null,
    about_developer: null,
    other_projects: null,
    why_recommended: null,
    coordinates: null,
  };
}

export default function CompoundPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { backendUrl, backendStatus, onRetryConnection, sessionId, setSessionId } = useOutletContext();

  // Arriving from Saved is a change of subject, not a continuation. The old
  // thread is an intake conversation about a different search; showing it next
  // to this property reads as a non-sequitur ("what's your budget?" under a
  // compound the client already saved). Start a new thread, and let the server
  // carry the requirement schema across so nothing is re-asked.
  const freshContext = !!useLocation().state?.freshContext;
  const [threadId, setThreadId] = useState(freshContext ? null : sessionId);
  const inheritSchemaFrom = freshContext ? sessionId : null;
  const loadedMessages = useSessionTranscript(threadId);

  // Once this screen creates its thread it becomes the shared one, so moving
  // on to another screen continues here rather than forking again.
  const handleThread = (id) => { setThreadId(id); setSessionId(id); };
  const [compound, setCompound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedImageIndex(0);
    api
      .get(`/compounds/${id}/details`)
      .then((res) => {
        if (!cancelled) setCompound(mapDetails(res.data));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err.response?.status === 404
              ? 'This compound could not be found.'
              : 'Unable to load compound details right now.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <div className="compound-page compound-state">Loading compound…</div>;
  }
  if (error || !compound) {
    return (
      <div className="compound-page compound-state">
        <p>{error || 'Compound not available.'}</p>
        <Link to="/" className="compound-btn secondary">Back to chat</Link>
      </div>
    );
  }

  // get_compound_details takes an int. A non-numeric id (CompoundCard falls
  // back to a name slug when compound_id is missing) cannot be resolved, so
  // send no focus at all rather than one the agent will fail on.
  const numericId = Number(compound.id ?? id);
  const focus = Number.isInteger(numericId)
    ? { type: 'compound', ids: [numericId], labels: [compound.project_name].filter(Boolean) }
    : null;

  const images = compound.images && compound.images.length > 0
    ? compound.images
    : [{ url: '', alt: compound.project_name }];
  const mainImage = images[selectedImageIndex];
  const mainImageUrl = typeof mainImage === 'string' ? mainImage : mainImage?.url;


  return (
    <div className="compound-page">
      <div className="compound-chat">
        <ChatContainer
          backendUrl={backendUrl}
          backendStatus={backendStatus}
          onRetryConnection={onRetryConnection}
          sessionId={threadId}
          onSessionChange={handleThread}
          inheritSchemaFrom={inheritSchemaFrom}
          loadedMessages={loadedMessages}
          showHeader={false}
          focus={focus}
          focusLabel={compound.project_name}
          onClearFocus={() => navigate('/')}
          suggestedChips={[
            // Arriving from Saved or Visits means this compound has a history
            // with the client — provenance makes that answerable, so offer it.
            ...(freshContext ? ['Why did this come up for me?'] : []),
            'Payment plans',
            'Delivery timeline',
            'Available unit types',
          ]}
        />
      </div>
      <aside className={`compound-panel${detailsOpen ? ' open' : ''}`}>
        <button
          type="button"
          className="compound-details-toggle"
          onClick={() => setDetailsOpen((o) => !o)}
          aria-expanded={detailsOpen}
        >
          <span>{detailsOpen ? 'Hide details' : 'Show full details'}</span>
          <span className="compound-details-caret" aria-hidden>{detailsOpen ? '\u25B4' : '\u25BE'}</span>
        </button>
        <div className="compound-gallery">
          {mainImageUrl ? (
            <div className="compound-gallery-main">
              <img src={mainImageUrl} alt={compound.project_name} />
            </div>
          ) : (
            <div className="compound-gallery-placeholder">No image</div>
          )}
          {images.length > 1 && (
            <div className="compound-gallery-thumbs">
              {images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  className={`thumb ${selectedImageIndex === i ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(i)}
                >
                  <img src={typeof img === 'string' ? img : img?.url} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>
        <section className="compound-details">
          <h2>{compound.project_name}</h2>
          <p className="compound-location">{compound.location}</p>
          <dl>
            <dt>Price from</dt>
            <dd>{compound.price_from || '—'}</dd>
            <dt>Unit types</dt>
            <dd>{compound.unit_types || '—'}</dd>
            <dt>Delivery</dt>
            <dd>{compound.delivery || '—'}</dd>
            <dt>Area</dt>
            <dd>{compound.area_range || '—'}</dd>
            <dt>Payment</dt>
            <dd>{compound.payment_summary || '—'}</dd>
            {compound.facilities && (
              <>
                <dt>Amenities</dt>
                <dd>{compound.facilities}</dd>
              </>
            )}
          </dl>
        </section>
        {compound.about_developer && (
          <section className="compound-section">
            <h3>About the developer</h3>
            <p>{compound.about_developer}</p>
            {compound.other_projects && (
              <p className="compound-other">Other projects: {compound.other_projects}</p>
            )}
          </section>
        )}
        {compound.why_recommended && (
          <section className="compound-section">
            <h3>Why this is a good choice</h3>
            <p>{compound.why_recommended}</p>
          </section>
        )}
        {compound.coordinates && (
          <section className="compound-section">
            <h3>Map</h3>
            <div className="compound-map-placeholder">Map (coordinates: {compound.coordinates})</div>
          </section>
        )}
        <div className="compound-actions">
          <button type="button" className="compound-btn primary" onClick={() => setShowVisitModal(true)}>Schedule visit</button>
          <button type="button" className="compound-btn secondary">Save</button>
          <Link to={`/compare?ids=${compound.id}`} className="compound-btn secondary">Compare</Link>
        </div>
      </aside>
      <ScheduleVisitModal
        isOpen={showVisitModal}
        onClose={() => setShowVisitModal(false)}
        compound={{ compound_id: compound.id, project_name: compound.project_name, location: compound.location }}
        onSuccess={() => setShowVisitModal(false)}
      />
    </div>
  );
}
